// What the house page reads: the connected owner's PAPER, the protocol's figures, and the few
// exact formats it shows them in. Every amount is a BigInt; floats only ever place a mark.

import { useQuery } from "@tanstack/react-query";
import { useConnection } from "wagmi";
import { pt, type IntentAction, type Operation } from "../../sdk";
import { useAccount, useOwner } from "../../state/account";
import { useTradingState } from "../../state/market";
import { big, fmt, fromRaw, paper, pctBp, ratioBp, usd } from "../../lib/num";

/** One cent, or one hundredth of a PAPER, at 18 decimals. */
const HUNDREDTH = 10n ** 16n;

export type Me =
  | { state: "disconnected" }
  | { state: "loading" }
  | {
      state: "ready";
      balance: bigint;
      staked: bigint;
      /** USDC at 18 decimals; null while neither the account nor the chain has said. */
      claimable: bigint | null;
      claimableFailed: boolean;
      claimed: bigint;
      /** Staking intents the relayer accepted and has not settled. */
      pending: Operation[];
    };

const STAKING: IntentAction[] = ["paperStake", "paperUnstake", "paperClaim"];

/** The connected owner's PAPER, with the chain's pending reward when the account has none yet. */
export function useMe(): Me {
  const { status } = useConnection();
  const owner = useOwner();
  const account = useAccount();
  const needsChain = owner !== null && account !== null && account.paper.claimable === null;
  const chain = useQuery({
    queryKey: ["pendingReward", owner],
    queryFn: () => pt.pendingReward(owner as string),
    enabled: needsChain,
    refetchInterval: 15_000,
  });
  // an explicit connect keeps the connect button (it shows its own progress); a reconnect on load waits
  if (status === "disconnected" || status === "connecting") return { state: "disconnected" };
  if (status !== "connected" || !owner || !account) return { state: "loading" };
  const claimableRaw = account.paper.claimable ?? (needsChain ? chain.data : undefined);
  return {
    state: "ready",
    balance: big(account.paper.balance),
    staked: big(account.paper.staked),
    claimable: claimableRaw === undefined ? null : big(claimableRaw),
    claimableFailed: needsChain && chain.isError,
    claimed: big(account.lifetime.paperRewardsClaimed),
    pending: account.operations.filter((operation) => STAKING.includes(operation.action)),
  };
}

/** Whether the relayer refuses `action` for now. */
export function usePaused(action: IntentAction): boolean {
  return useTradingState().data?.pausedActions.includes(action) ?? false;
}

/** The least PAPER one stake moves, or null before the trading state says. */
export function useStakeMinimum(): bigint | null {
  const raw = useTradingState().data?.paperStakeMinimum;
  return raw ? big(raw) : null;
}

/** Dollars at 18 decimals, cents shown; a dust amount reads `<$0.01`. `compact` abbreviates from that many whole digits. */
export function dollars(raw: bigint, compact?: number): string {
  if (raw > 0n && raw < HUNDREDTH) return "<$0.01";
  return compact === undefined ? usd(raw) : usd(raw, { compact });
}

/** Dollars abbreviated from $1K, trailing zeros dropped: `$2M`, `$4.97M`, `−$1.2M`. */
export function dollarsShort(raw: bigint): string {
  return usd(raw, { compact: 4, places: 2, min: 0 });
}

/** PAPER at two places; `compact` abbreviates from that many whole digits. */
export function papers(raw: bigint, compact?: number): string {
  if (raw > 0n && raw < HUNDREDTH) return "<0.01";
  return compact === undefined ? paper(raw) : paper(raw, { compact });
}

/** PAPER exactly as typed, grouped: for a button that says what it sends. */
export function papersExact(raw: bigint): string {
  return fmt(fromRaw(raw, 18), { places: 18, min: 0 });
}

/** `part` of `whole` as a percentage; a share too small for 0.01% reads `<0.01%`. */
export function shareOf(part: bigint, whole: bigint): string {
  const bp = ratioBp(part, whole);
  return part > 0n && bp === 0n ? "<0.01%" : pctBp(bp);
}

/** A mint rate (PAPER per dollar, 18 decimals) at two places. */
export function rateText(raw: string | bigint): string {
  return fmt(fromRaw(raw, 18), { places: 2 });
}

/** `a / b` as a float in [0, 1], for placing a mark only. */
export function frac(a: bigint, b: bigint): number {
  if (b <= 0n || a <= 0n) return 0;
  if (a >= b) return 1;
  return Number((a * 1_000_000n) / b) / 1_000_000;
}
